import React, { useState } from "react";
import styles from "../IndexRegistration.module.scss";
import { cookieHelpers } from "helpers/cookieHelpers";
import { requestJson, RequestMethod } from "helpers/httpHelper";
import { toast } from "react-toastify";

const dialCodes = {
  ZZGN: "Gaze naturale",
  EA: "Energie electrică",
  EAN: "Energie electrică - nocturnă",
  EAZ: "Energie electrică - zi",
}

export default function Step2({ setCurrentStep, stepsState, persistState }) {
  const [locations, setLocations] = useState(stepsState.step2?.locations || []);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleIndexChange = (id, value) => {
    const updatedLocations = locations.map((loc) =>
      loc.id === id ? { ...loc, index_nou: value, validated: false } : loc
    );
    setLocations(updatedLocations);

    persistState((prevState) => ({
      ...prevState,
      step2: {
        locations: updatedLocations,
      },
    }));
  };

  const handleValidateRow = (id) => {
    const location = locations.find((loc) => loc.id === id);
    if (!location) return;

    // Validate that new index is >= old index
    const newIndex = parseInt(location.index_nou);
    const oldIndex = parseInt(location.index_vechi);

    if (isNaN(newIndex) || newIndex < oldIndex) {
      toast.error("Indexul facturat este mai mic decât indexul introdus!");
    }

    // Mark as validated locally
    const updatedLocations = locations.map((loc) =>
      loc.id === id ? { ...loc, validated: true } : loc
    );
    setLocations(updatedLocations);

    // Persist to sessionStorage
    persistState((prevState) => ({
      ...prevState,
      step2: {
        locations: updatedLocations,
      },
    }));
  };

  const handleBack = () => {
    setCurrentStep(1);
  };

  const handleSubmit = async () => {
    // Filter validated locations
    const validatedLocations = locations.filter((loc) => loc.validated);

    if (validatedLocations.length === 0) {
      toast.error("Vă rugăm să validați cel puțin un index");
      return;
    }

    setIsSubmitting(true);

    try {
      // Build readings array
      const readings = validatedLocations.map((loc) => ({
        metering_point_no: loc.metering_point_no,
        new_index: parseInt(loc.index_nou),
        old_index: parseInt(loc.index_vechi),
        meter_no_series: loc.serie_contor,
        dial_code: loc.dial_code,
        unit_of_measure: loc.unit_of_measure || "kWh",
      }));

      // Get customer_no from first location (all have the same customer)
      const customer_no = validatedLocations[0].customer_no;

      // Call API with all readings
      const { response, isSuccess } = await requestJson(
        "account/self-reading",
        RequestMethod.POST,
        {
          data: {
            customer_no,
            readings,
          },
        }
      );

      if (!isSuccess) {
        setIsSubmitting(false);
        return;
      }

      // Handle response
      if (response?.success) {
        toast.success(response.message || "Toate citiirile au fost adăugate cu succes.");
      } else {
        // Show individual results if some failed
        const failedReadings = response?.results?.filter((r) => !r.success) || [];
        if (failedReadings.length > 0) {
          failedReadings.forEach((reading) => {
            toast.error(`${reading.metering_point_no}: ${reading.message}`);
          });
        }
      }

      // Clear session and move to success page
      cookieHelpers.clearAllSessionData();
      setCurrentStep(3);
    } catch (error) {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="pb-55">
      <div className={styles.indexContainer}>
        <h2 className={styles.stepTitle}>
          Verifică datele și înregistrează indexul nou pentru acest loc de consum
        </h2>

          {/* Desktop table view */}
          <div className={`${styles.tableContainer} ${styles.desktopOnly}`}>
            <table className="table table-bordered">
              <thead>
                <tr>
                  <th>Nr.</th>
                  <th>Serie contor</th>
                  <th width="180" style={{textAlign: 'center'}}>Adresa</th>
                  <th>Index nou</th>
                  <th>*</th>
                </tr>
              </thead>
              <tbody>
                {locations.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center py-4">
                      Nu există locații de afișat
                    </td>
                  </tr>
                ) : (
                  locations.map((location, index) => (
                    <tr key={location.id}>
                      <td>{index + 1}</td>
                      <td>
                        <div style={{fontSize: 12}}>
                          <b>{dialCodes[location.dial_code] ? `${dialCodes[location.dial_code]}` : ''}</b>
                        </div>
                        <span style={{ fontSize: 14}}>{location.serie_contor}</span>
                      </td>
                      <td>
                        <div style={{fontSize: 12, lineHeight: "16px"}}>{location.address}</div>
                      </td>
                      <td>
                        <input
                          type="number"
                          id={`index-nou-${location.id}`}
                          name={`index_nou_${location.id}`}
                          className="form-control"
                          value={location.index_nou}
                          onChange={(e) =>
                            handleIndexChange(location.id, e.target.value)
                          }
                          disabled={location.validated}
                          placeholder={location.unit_of_measure}
                        />
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-success btn-sm"
                          onClick={() => handleValidateRow(location.id)}
                          disabled={!location.index_nou || location.validated}
                        >
                          {location.validated ? "Validat" : "Validează"}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile card view */}
          <div className={styles.mobileOnly}>
            {locations.length === 0 ? (
              <div className="text-center py-4">Nu există locații de afișat</div>
            ) : (
              locations.map((location, index) => (
                <div key={location.id} className={styles.meterCard}>
                  <div className={styles.cardHeader}>
                    <span className={styles.cardNumber}>#{index + 1}</span>
                    <div className={styles.cardTitle}>
                      <div style={{fontSize: 12, fontWeight: 'bold'}}>
                        {dialCodes[location.dial_code] ? `${dialCodes[location.dial_code]}` : ''}
                      </div>
                      <div style={{fontSize: 12}}><b>Serie contor:</b> {location.serie_contor}</div>
                    </div>
                  </div>

                  {location.address && (
                    <div className={styles.cardAddress}>
                      <strong>Adresă:</strong> {location.address}
                    </div>
                  )}

                  <div className={styles.cardInputRow}>
                    <input
                      type="number"
                      id={`index-nou-mobile-${location.id}`}
                      name={`index_nou_mobile_${location.id}`}
                      className="form-control"
                      value={location.index_nou}
                      onChange={(e) => handleIndexChange(location.id, e.target.value)}
                      disabled={location.validated}
                      placeholder={`Index nou (${location.unit_of_measure})`}
                      style={{ flex: 1, marginRight: '10px' }}
                    />
                    <button
                      type="button"
                      className="btn btn-success"
                      onClick={() => handleValidateRow(location.id)}
                      disabled={!location.index_nou || location.validated}
                      style={{ minWidth: '100px' }}
                    >
                      {location.validated ? "Validat" : "Validează"}
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="d-flex justify-content-between mt-4">
            <button
              type="button"
              onClick={handleBack}
              className="btn btn-danger"
            >
              Înapoi
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              className="btn btn-success"
              disabled={locations.length === 0 || isSubmitting}
            >
              {isSubmitting ? "Se transmit..." : "Transmite"}
            </button>
          </div>
      </div>
    </section>
  );
}
